import React, { Suspense, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Store, 
  ShoppingCart, 
  Award, 
  BookOpen, 
  Users, 
  Calendar,
  ArrowLeft,
  GraduationCap,
  TrendingUp,
  BarChart,
  Package,
  DollarSign
} from 'lucide-react';
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
  Stars,
  PerspectiveCamera
} from "@react-three/drei";

// Préchargement du modèle
useGLTF.preload("/models/free_ice_cream_shop.glb");

// Modèle 3D
function IceCreamShopModel() {
  const { scene } = useGLTF("/models/free_ice_cream_shop.glb");
  const ref = useRef();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    ref.current.rotation.y = t * 0.2;
  });
  
  return (
    <primitive
      ref={ref}
      object={scene}
      scale={[0.5, 0.5, 0.5]}
      position={[0, -1, 0]}
    />
  );
}

// Scène 3D
function Scene3D() {
  return (
    <Canvas
      shadows
      className="fixed inset-0 -z-10"
      dpr={[1, 2]}>
      <color attach="background" args={["#1e1e2e"]} />
      <fog attach="fog" args={["#1e1e2e", 5, 15]} />
      <ambientLight intensity={0.5} />
      <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
      
      <Suspense fallback={null}>
        <PerspectiveCamera makeDefault position={[0, 1, 10]} fov={40} />
        
        <Stage environment="city">
          <Center>
            <IceCreamShopModel />
          </Center>
        </Stage>
        
        <Environment preset="city" />
        <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.5} />
        <Stars count={5000} depth={50} fade speed={1.5} />
      </Suspense>
    </Canvas>
  );
}

const VenteGestion = () => {
  const formations = [
    {
      icon: <ShoppingCart className="w-8 h-8 text-blue-400" />,
      title: "CAP Équipier Polyvalent du Commerce (EPC)",
      duration: "2 ans",
      niveau: "Niveau 3 (CAP)",
      description: "Formation complète aux techniques de vente et de conseil client dans le commerce.",
      competences: [
        "Réception et suivi des commandes",
        "Mise en rayon et merchandising",
        "Conseil et vente auprès des clients",
        "Tenue de caisse et encaissement"
      ]
    },
    {
      icon: <Store className="w-8 h-8 text-indigo-400" />,
      title: "Bac Pro Métiers du Commerce et de la Vente (MCV)",
      duration: "3 ans",
      niveau: "Niveau 4 (Bac)",
      description: "Formation avancée aux métiers du commerce avec une spécialisation possible.",
      competences: [
        "Animation et dynamisation de l'offre commerciale",
        "Gestion des produits et des stocks",
        "Relation client et négociation commerciale",
        "Management d'une équipe commerciale"
      ]
    }
  ];

  const debouches = [
    {
      icon: <Users className="w-6 h-6 text-blue-400" />,
      title: "Vendeur en magasin",
      description: "Conseiller et vendre des produits en magasin"
    },
    {
      icon: <Package className="w-6 h-6 text-indigo-400" />,
      title: "Chef de rayon",
      description: "Gérer un rayon et animer une équipe"
    },
    {
      icon: <BarChart className="w-6 h-6 text-purple-400" />,
      title: "Responsable de magasin",
      description: "Diriger et gérer un point de vente"
    },
    {
      icon: <DollarSign className="w-6 h-6 text-cyan-400" />,
      title: "Attaché commercial",
      description: "Développer un portefeuille clients"
    }
  ];

  const avantages = [
    {
      icon: <TrendingUp className="w-6 h-6 text-green-400" />,
      title: "Secteur dynamique",
      description: "Un domaine en constante évolution avec de nombreuses opportunités"
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-yellow-400" />,
      title: "Formation pratique",
      description: "Alternance entre théorie et pratique en entreprise"
    },
    {
      icon: <Award className="w-6 h-6 text-orange-400" />,
      title: "Diplômes reconnus",
      description: "Certifications professionnelles valorisées sur le marché"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen py-40 bg-gradient-to-br from-slate-900 to-slate-800"
    >
      {/* Scène 3D en arrière-plan */}
      <div className="fixed inset-0 z-0">
        <Scene3D />
      </div>
      
      <Loader />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Bouton retour */}
        <Link to="/">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mb-8 px-6 py-3 bg-slate-800/60 backdrop-blur-sm text-white rounded-lg flex items-center space-x-2 hover:bg-slate-700/60 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </motion.button>
        </Link>

        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <Store className="w-16 h-16 text-blue-400" />
            <h1 className="text-5xl font-bold text-white">Pôle Vente Gestion</h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Formez-vous aux métiers du commerce et développez vos compétences en vente, conseil client et gestion commerciale
          </p>
        </motion.div>

        {/* Formations */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Nos Formations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {formations.map((formation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="bg-slate-800/60 backdrop-blur-sm p-8 rounded-lg border-l-4 border-blue-500 hover:shadow-lg hover:shadow-blue-500/20 transition-all"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {formation.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-white mb-2">{formation.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-3 py-1 bg-blue-600/30 text-blue-300 rounded-full text-sm border border-blue-400/30">
                        {formation.duration}
                      </span>
                      <span className="px-3 py-1 bg-purple-600/30 text-purple-300 rounded-full text-sm border border-purple-400/30">
                        {formation.niveau}
                      </span>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6">{formation.description}</p>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-white flex items-center space-x-2">
                    <BookOpen className="w-5 h-5 text-blue-400" />
                    <span>Compétences développées :</span>
                  </h4>
                  <ul className="space-y-2">
                    {formation.competences.map((comp, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-gray-300">
                        <span className="text-blue-400 mt-1">•</span>
                        <span>{comp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Débouchés */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Débouchés Professionnels</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {debouches.map((debouche, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-slate-800/60 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-blue-500/20 transition-all"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {debouche.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{debouche.title}</h3>
                <p className="text-gray-300 text-sm">{debouche.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Avantages */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Pourquoi choisir nos formations ?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {avantages.map((avantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-slate-800/60 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-blue-500/20 transition-all"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {avantage.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{avantage.title}</h3>
                <p className="text-gray-300 text-sm">{avantage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-lg font-semibold"
            >
              Nous contacter pour plus d'informations
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default VenteGestion;